/* eslint-disable no-restricted-syntax */
/* eslint-disable no-labels */
/* Copyright (c) 2020-present CloudIO Inc */

import { evalX } from '@cloudio-saas/quickx';
import { selector, useStateXSelector } from '@cloudio-saas/statex';
import {
  useCollectionRecord,
  useCollectionRowIndex,
  useRowIndex,
} from 'common/Context';
import type {
  BooleanExpressionValue,
  ExpressionValue,
} from 'componentregistry';
import type { Platform } from 'componentregistry/platform/Platform';
import { useCurrentDataStore } from 'components/DataStoreReference/DataStoreReference';
import stringify from 'json-stable-stringify';
import IOError from 'platform/IOError';
import { usePlatform } from 'platform/PlatformHooks';
import { useUpdateOnHMR } from 'utils/hmr';
import { getErrorMessage, isEmpty, isNull, NOT_DEFINED } from 'utils/isEmpty';
import Logger from 'utils/Logger';
import PlatformVariableProvider, { Context } from './VariableProvider';

export type { Context } from './VariableProvider';
export {
  evalBoolean,
  evalNumber,
  evalString,
  evalUnknown,
  useBooleanExpression,
  useExpression,
  useExpressionWithError,
  useNumberExpression,
  useStringExpression,
};
export type Props = {
  exp: ExpressionValue | BooleanExpressionValue;
  context: Omit<Context, 'dsAlias'>;
  donotShowError?: boolean;
};

export type ExpressionResult = {
  value: string | number | boolean | undefined | null;
  error: string | undefined;
};

const EMPTY_EXPRESSION_RESULT: ExpressionResult = Object.freeze({
  value: undefined,
  error: undefined,
});

const expressionSelector = selector<
  ExpressionResult,
  { platform: Platform; data: Record<string, unknown> }
>({
  path: [
    'transient',
    'expressionSelector',
    ':appUid',
    ':pageId',
    ':itemId',
    ':property',
    ':dsAlias',
    ':rowIndex',
    ':collectionRowIndex',
    ':exp',
  ],
  get: ({ get, params, getRef }, { data, platform }): ExpressionResult => {
    const { exp, appUid, pageId, itemId, property, rowIndex, dsAlias } =
      params as {
        exp: string;
        appUid: string;
        pageId: string;
        dsAlias: string;
        itemId: string;
        property: string;
        rowIndex: number;
      };
    if (exp !== 'x') {
      if (!platform) {
        // this should never happen
        throw Error('Missing platform ref!');
      }
      try {
        const pp = new PlatformVariableProvider(
          platform,
          {
            appUid,
            pageId,
            itemId,
            rowIndex,
            dsAlias,
            property,
            data,
          },
          get,
        );
        const value = evalX(exp, pp);
        if (value === undefined) {
          return EMPTY_EXPRESSION_RESULT;
        }
        return { value, error: undefined };
      } catch (e) {
        if (!data.donotShowError) {
          platform.showError(
            e instanceof Error
              ? `${e.name} evaluating expression [${exp}]: ${e.message}`
              : getErrorMessage(e),
            {
              appUid,
              pageId,
              itemId,
            },
          );
        }
        return { value: undefined, error: getErrorMessage(e) };
      }
    }
    return EMPTY_EXPRESSION_RESULT;
  },
});

function useExpressionWithError({
  exp,
  context,
  donotShowError,
}: Props): ExpressionResult {
  useUpdateOnHMR();
  const recordIndex = useRowIndex();
  const cr = useCollectionRecord();
  const collectionRowIndex = useCollectionRowIndex();
  const store = useCurrentDataStore();
  const platform = usePlatform();
  let rowIndex: number | undefined = context.rowIndex ?? recordIndex;
  rowIndex = !isNull(rowIndex) ? rowIndex : undefined;
  const { value, error } = useStateXSelector(expressionSelector, {
    params: {
      exp: exp.type === 'expression' ? exp.value ?? 'x' : 'x',
      appUid: context.appUid,
      pageId: context.pageId,
      itemId: context.itemId,
      dsAlias: store?.alias ?? NOT_DEFINED,
      property: context.property,
      rowIndex: rowIndex ?? -1,
      collectionRowIndex,
    },
    props: {
      data: {
        ...cr,
        ...context.data,
        donotShowError,
        rowIndex,
        collectionRowIndex,
      },
      platform,
    },
  });

  if (typeof exp === 'string') {
    return { value: exp, error: undefined };
  }
  if (typeof exp === 'boolean') {
    return { value: exp, error: undefined };
  }
  if (exp.type === 'text') {
    return { value: exp.value, error: undefined };
  }
  if (exp.type === 'boolean') {
    return { value: exp.value, error: undefined };
  }
  return { value, error };
}

function useExpression({ exp, context }: Props): unknown {
  const { value } = useExpressionWithError({ exp, context });
  return value;
}

/**
 * Evaluates the given expression using the context and return the evaluated string
 *
 * This hook will re-render the component whenever variables used
 * in the expression changes.
 *
 * e.g. if the expression is \`${$.dsAlias.firstName}\`, then the component
 * will rerender whenever the `firstName` changes in the store with alias `dsAlias`.
 * This could be due to user typing in a text field that is bound to the firstName
 * attribute or if the user changes the active record of the store by clicking
 * on a different row in a grid.
 *
 * @param props - The props
 * @param props.exp - The {@link ExpressionValue} to be evaluated
 * @param props.context - The {@link Context} to be used
 * @returns the evaluated string value
 */
function useStringExpression(props: Props): string | null | undefined {
  const value = useExpression(props);
  if (isNull(value) || typeof value === 'string') {
    return value;
  }
  return String(value);
}

/**
 * Evaluates the given expression using the context and return the evaluated boolean value
 *
 * This hook will re-render the component whenever variables used
 * in the expression changes.
 *
 * e.g. if the expression is `$.dsAlias.amount} > 100`, then the component
 * will rerender whenever the `amount` changes in the store with alias `dsAlias`.
 * This could be due to user typing in a number field that is bound to the amount
 * attribute or if the user changes the active record of the store by clicking
 * on a different row in a grid.
 *
 * @param props - The props
 * @param props.exp - The {@link ExpressionValue} to be evaluated
 * @param props.context - The {@link Context} to be used
 * @returns the evaluated boolean value
 */
function useBooleanExpression(props: Props, defaultValue = false): boolean {
  const value = useExpression(props);
  if (typeof value === 'boolean') {
    return value;
  }
  return defaultValue;
}

/**
 * Evaluates the given expression using the context and return the evaluated number
 *
 * This hook will re-render the component whenever variables used
 * in the expression changes.
 *
 * e.g. if the expression is `$.dsAlias.amount`, then the component
 * will rerender whenever the `amount` changes in the store with alias `dsAlias`.
 * This could be due to user typing in a number field that is bound to the amount
 * attribute or if the user changes the active record of the store by clicking
 * on a different row in a grid.
 *
 * @param props - The props
 * @param props.exp - The {@link ExpressionValue} to be evaluated
 * @param props.context - The {@link Context} to be used
 * @returns the evaluated number value
 */
function useNumberExpression(props: Props, defaultValue = 0): number {
  const value = useExpression(props);
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const val = Number(value);
    if (!Number.isNaN(val)) {
      return val;
    }
  }
  return defaultValue;
}

function _eval(platform: Platform, context: Context, exp: string): unknown {
  // exp must always be an expression. no string allowed similar to 3.0
  // if (!isExpression(exp)) {
  //   return exp;
  // }
  try {
    let result = evalX(exp, new PlatformVariableProvider(platform, context));
    if (typeof result === 'number' && Number.isNaN(result)) {
      result = null;
    }
    if (Logger.isLogEnabled()) {
      // @ts-ignore platform
      const { platform: _p, store: _s, data, ...others } = context;
      const { platform: _p2, store: _s2, children, ...otherData } = data;
      try {
        Logger.log({
          status: 'trace',
          itemType: 'evalX',
          msg: `Expression [${exp}] withContext: ${stringify({
            ...others,
            data: otherData,
          })} = ${stringify(result)}`,
          itemId: context.itemId,
          onRender: true,
        });
      } catch (e) {
        console.error(
          `Error loggin expression [${exp}]: ${getErrorMessage(e)}`,
        );
      }
    }
    return result;
  } catch (e) {
    if (e instanceof Error) {
      throw new IOError(
        e.name,
        `Error evaluating expression [${exp}]: ${e.message}`,
      ).withItem({
        appUid: context.appUid,
        pageId: context.pageId,
        itemId: context.itemId,
      });
    }
    throw e;
  }
}

function evalBoolean(
  platform: Platform,
  context: Context,
  exp?: string,
): boolean {
  if (isEmpty(exp)) {
    return true;
  }
  const { appUid, pageId, itemId } = context;
  if (typeof exp !== 'string') {
    platform.showError(
      `Invalid expression type '${typeof exp}'. Must be string!`,
      { appUid, pageId, itemId },
    );
    return false;
  }
  const result = _eval(platform, context, exp);
  if (typeof result === 'boolean') {
    return result;
  }
  platform.showError(
    `Invalid result from expression [${exp}]. Expected boolean but got [${result}] of type ${typeof result}!`,
    { appUid, pageId, itemId },
  );
  return false;
}

function evalString(
  platform: Platform,
  context: Context,
  exp?: string,
): string | null {
  if (isEmpty(exp)) {
    return null;
  }
  const { appUid, pageId, itemId } = context;
  if (typeof exp !== 'string') {
    platform.showError(
      `Invalid expression type '${typeof exp}'. Must be string!`,
      { appUid, pageId, itemId },
    );
    return null;
  }
  const result = _eval(platform, context, exp);
  if (isNull(result)) {
    return null;
  }
  if (typeof result !== 'string') {
    return String(result);
  }
  return result;
}

function evalNumber(
  platform: Platform,
  context: Context,
  exp?: string,
): number | null {
  if (isEmpty(exp)) {
    return null;
  }
  const { appUid, pageId, itemId } = context;
  if (typeof exp !== 'string') {
    platform.showError(
      `Invalid expression type '${typeof exp}'. Must be string!`,
      { appUid, pageId, itemId },
    );
    return null;
  }
  const result = _eval(platform, context, exp);
  if (isNull(result)) {
    return null;
  }
  if (typeof result === 'number') {
    return result;
  }
  throw Error(
    `Invalid result from expression [${exp}]. Expected number but got [${result}] of type ${typeof result}!`,
  );
}

function evalUnknown(
  platform: Platform,
  context: Context,
  exp?: string,
): unknown | null {
  if (isEmpty(exp)) {
    return null;
  }
  const { appUid, pageId, itemId } = context;
  if (typeof exp !== 'string') {
    platform.showError(
      `Invalid expression type '${typeof exp}'. Must be string!`,
      { appUid, pageId, itemId },
    );
    return null;
  }
  const result = _eval(platform, context, exp);
  if (result === undefined) {
    return null;
  }
  return result;
}
